import {
  NativeCouponTrancheI,
  NativeCouponViewI,
} from "../api/request/Coupons/Response/NativeCoupon.response";

// Copy del cupón del funnel native. Native es solo IT, así que va en italiano
// sin pasar por translations (como NativeRenewalConfirmation).

// 10 → «10»; 12,5 % → «12,5»; 25,50 € → «25,50».
const decimal = (n: number, trimZero: boolean): string => {
  if (Number.isInteger(n)) {
    return String(n);
  }
  const fixed = n.toFixed(2);
  return (trimZero ? fixed.replace(/0$/, "") : fixed).replace(".", ",");
};

const euros = (cents: number): string => `${decimal(cents / 100, false)} €`;

// «1° anno», «2°–3° anno», «dal 4° anno» (igual con «mese»).
const ordinals = (from: number, to: number | null, unit: string): string => {
  if (null === to) {
    return `dal ${from}° ${unit}`;
  }
  return from === to ? `${from}° ${unit}` : `${from}°–${to}° ${unit}`;
};

const singleMonth = (t: NativeCouponTrancheI): boolean =>
  null !== (t.fromMonth ?? null) && t.fromMonth === t.toMonth;

const when = (t: NativeCouponTrancheI): string => {
  const years = ordinals(t.fromAnnuality, t.toAnnuality, "anno");
  // Sin meses = toda la anualidad.
  return null === (t.fromMonth ?? null)
    ? years
    : `${years}, ${ordinals(t.fromMonth as number, t.toMonth ?? null, "mese")}`;
};

// Ninguna promo deja un recibo a 0 €: los meses gratis y el importe fijo se
// reparten entre los recibos de la anualidad.
const discount = (t: NativeCouponTrancheI): string => {
  switch (t.kind) {
    case "free_months":
      return `${1 === t.value ? "1 mese gratis" : `${t.value} mesi gratis`} (ripartiti)`;
    case "fixed_amount":
      return `-${euros(t.value)}`;
    case "fixed_price":
      return singleMonth(t) ? euros(t.value) : `${euros(t.value)}/mese`;
    default:
      return `-${decimal(t.value / 100, true)}%`;
  }
};

/**
 * Resumen del plan por años: «1° anno: -10%», «2°–3° anno: -15%»,
 * «1° anno: 2 mesi gratis (ripartiti)», «2° anno: -50 €»,
 * «1° anno, 1°–3° mese: 1 €/mese», «1° anno, 6° mese: 5 €».
 */
export const couponPlanLines = (plan: NativeCouponTrancheI[]): string[] =>
  [...plan]
    .sort(
      (a, b) =>
        a.fromAnnuality - b.fromAnnuality || (a.fromMonth ?? 0) - (b.fromMonth ?? 0)
    )
    .map((tranche) => `${when(tranche)}: ${discount(tranche)}`);

/** Un lead que no admite cupones (alta de caller) va sin casilla de cupón. */
export const visibleCoupon = (view: NativeCouponViewI): NativeCouponViewI | null =>
  false === view.eligible ? null : view;

/** Solo un cupón propio y sin congelar: el de por defecto no se quita. */
export const couponRemovable = (view: NativeCouponViewI): boolean =>
  null !== view.coupon && !view.coupon.isDefault && !view.redeemed;

/**
 * El código de campaña con el que llega el lead (`gtf_campaign` de la URL o de
 * su cookie, p. ej. `segugio10`), como en legacy al cotizar. En native se aplica
 * en ms-agl salvo que sea el de por defecto del mercado (ya lo lleva) o
 * `neopension` (no es una promo).
 */
export const campaignCouponToApply = (
  fromUrl: string | null,
  fromCookie: string | null | undefined,
  marketDefault: string | null | undefined
): string | null => {
  const code = (fromUrl || fromCookie || "").trim();
  if ("" === code || "neopension" === code.toLowerCase()) {
    return null;
  }

  return marketDefault && code.toUpperCase() === marketDefault.toUpperCase() ? null : code;
};

/** No se pisa un cupón que el lead ya eligió ni uno congelado al pagar. */
export const keepsCurrentCoupon = (view: NativeCouponViewI): boolean =>
  false === view.eligible || view.redeemed || (null !== view.coupon && !view.coupon.isDefault);

const ERRORS: Record<string, string> = {
  COUPON_NOT_FOUND: "Codice non valido. Controlla di averlo scritto correttamente.",
  INVALID_CODE: "Inserisci un codice valido.",
  COUPON_NOT_ACTIVE: "Questo codice non è attivo.",
  COUPON_NOT_YET_VALID: "Questo codice non è ancora valido.",
  COUPON_EXPIRED: "Questo codice è scaduto.",
  COUPON_EXHAUSTED: "Questo codice ha raggiunto il numero massimo di utilizzi.",
  COUPON_WRONG_CHANNEL: "Questo codice non è valido per questo acquisto.",
  COUPON_ALREADY_USED: "Hai già utilizzato questo codice.",
  COUPON_LOCKED: "Il prezzo è già confermato: non è più possibile cambiare il codice.",
  COUPON_NOT_ELIGIBLE: "Questo codice non è valido per questo acquisto.",
  COUPON_PRICE_REFRESH_FAILED: "Non siamo riusciti ad aggiornare il prezzo. Riprova.",
  COUPONS_NOT_ENABLED: "Al momento non è possibile applicare codici promozionali.",
};

export const couponErrorMessage = (code?: string): string =>
  ERRORS[code ?? ""] ?? "Non è stato possibile applicare il codice. Riprova più tardi.";
